import { cn } from "@fieldwork/ui/lib/utils";
import type { ReactNode, SelectHTMLAttributes } from "react";

export function Select({ className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(
        "h-10 w-full rounded-[4px] border border-input bg-background px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring/50",
        className,
      )}
      {...props}
    />
  );
}

const OK = "bg-[rgba(95,191,127,0.14)] text-[#7FD39B]";
const INFO = "bg-[rgba(125,167,240,0.14)] text-[#A7C2F7]";
const WARN = "bg-[rgba(232,163,61,0.14)] text-[#F0B65A]";
const BAD = "bg-[rgba(240,100,90,0.14)] text-[#F48A80]";
const HOT = "bg-[rgba(255,79,26,0.16)] text-[#FF8A63]";
const tones: Record<string, string> = {
  hot: HOT,
  online: OK,
  installed: OK,
  active: OK,
  ok: OK,
  applied: OK,
  booting: INFO,
  creating: INFO,
  installing: INFO,
  info: INFO,
  offline: WARN,
  deleting: WARN,
  running: WARN,
  warn: WARN,
  error: BAD,
  failed: BAD,
};

export function Chip({ value, label }: { value: string; label?: string }) {
  return (
    <span className={cn("inline-block rounded-[4px] px-2 py-0.5 font-mono text-[11px]", tones[value] ?? "bg-[#2A2927] text-[#CFCBC1]")}>
      {label ?? value}
    </span>
  );
}

/** An account page under the top header (Servers, Cloud tokens, Admin…), as on the design canvas. */
export function Page({
  title,
  subtitle,
  actions,
  back,
  width = "max-w-[1120px]",
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  back?: ReactNode;
  /** Tailwind max-width class for the column */
  width?: string;
  children: ReactNode;
}) {
  return (
    <main className={cn("mx-auto box-border flex w-full flex-col gap-6 px-5 pt-8 pb-16 sm:px-10", width)}>
      {back}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="m-0 font-mono text-[26px] font-bold tracking-[-0.02em]">{title}</h1>
          {subtitle && <div className="m-0 text-[13px] text-[#A8A59D]">{subtitle}</div>}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
      {children}
    </main>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5 text-sm">
      <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}

export function ago(ts: string | number | Date | null | undefined): string {
  if (!ts) return "never";
  const s = Math.round((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

const FIELD_LABELS: Record<string, string> = {
  name: "Server name",
  ownerName: "Your name",
  ownerEmail: "Your email",
  timezone: "Timezone",
  sshPublicKey: "SSH public key",
  serverType: "Size",
  location: "Location",
  token: "Token",
  label: "Label",
};

/** The server's message, or for input validation errors, which field and why. */
export function errorMessage(e: unknown): string {
  const issues = (e as { data?: { issues?: { path?: (string | number | { key: string })[]; message: string }[] } })?.data
    ?.issues;
  if (issues?.length) {
    return issues
      .map((i) => {
        const key = i.path?.map((p) => (typeof p === "object" ? p.key : p)).join(".") ?? "";
        return `${FIELD_LABELS[key] ?? key}: ${i.message}`;
      })
      .join("\n");
  }
  return e instanceof Error ? e.message : String(e);
}

/** What Hetzner accepts as a server name: lowercase letters, digits and single dashes. */
export function toServerName(v: string): string {
  return v
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+/, "")
    .slice(0, 40);
}

/** Radio cards (the canvas's "Suggest only / Apply changes" picker). Changing the choice calls onChange. */
export function ChoiceGroup<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: T;
  options: { value: T; title: string; body: ReactNode }[];
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="grid grid-cols-[repeat(auto-fit,minmax(min(240px,100%),1fr))] gap-2.5">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <label
            key={o.value}
            className={cn(
              "flex cursor-pointer gap-3 rounded-md px-4 py-3.5",
              on ? "bg-[#222220] shadow-[inset_0_0_0_1.5px_#FF4F1A]" : "bg-[#161615] shadow-[inset_0_0_0_1px_#34332F]",
              disabled && "cursor-wait opacity-70",
            )}
          >
            <input
              type="radio"
              name={label}
              className="mt-[3px] size-4 shrink-0 accent-[#FF4F1A]"
              checked={on}
              disabled={disabled}
              onChange={() => !on && onChange(o.value)}
            />
            <span className="flex flex-col gap-1">
              <span className="font-semibold">{o.title}</span>
              <span className="text-[13px] text-[#A8A59D]">{o.body}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
}
